import axiosInstance, {
  establishSessionWithinLock,
  LoginOutcome,
  postRecoveringInterruptedRefresh,
  postWebSession,
  refreshAccessToken,
  SessionChangedError,
  sidOf,
  webSessionPath,
  withSessionLock,
} from 'config/axiosInstance';
import { markLeavingSupport, SupportClaims, supportClaimsOf } from 'config/supportSession';
import {
  getAccessToken,
  getSessionIdentity,
  hasUsableAccess,
  isSessionReplaced,
} from 'config/session';
import { newRequestId } from 'components/Driver/registerDriver';

/**
 * YAC-2026-009 PR7b。運営のサポート（運営が「この会社の管理者として入る」）の管理画面の側。サーバーは PR7a（#393）。
 * 画面の部品はこのファイルの関数だけを使い、API を直に呼ばない（利用側の契約 admin-flow-consumers.json の sources）。
 *
 * セッションの切り替え（更新用トークンは HttpOnly の Cookie。タブ間で共有）:
 * - 入る（POST /v1/web/sessions/<運営の sid>/support/enter）: 運営の access（Bearer）と Cookie で、サポートのセッション（その会社の
 *   管理者。act manager）に切り替わる。Cookie はサーバーが入れ替える。応答の access をログインと同じ確かめ
 *   （establishSessionWithinLock。プロフィールの役割・Cookie で更新できるか）に通してから使う。
 * - 戻る（POST /v1/web/sessions/<サポートの sid>/support/return）: Cookie だけで、入ったときの運営のセッションに戻る（2段階認証なし）。
 *   end が true なら、記録も閉じる（「戻ってサポートを終わらせる」）。
 * どちらも Cookie を書き換えるので、ログイン・更新・ログアウトと同じ錠（withSessionLock）の中で送る。
 * EV-491。経路はそのときのセッションの sid の下（webSessionPath）。応答の Cookie は切り替わった先のセッションの Path に
 * 書かれるので、離れた画面の更新の応答を待たない（前のセッションの Path の Cookie しか書けない）。
 */
const OPERATOR_SUPPORT_URL = '/v1/operator/support_accesses/';
const COMPANY_SUPPORT_URL = '/v1/company/support_accesses/';

export const SUPPORT_PAGE_SIZE = 10;

export type SupportStatus = 'active' | 'ended' | 'expired';
export type SupportEndReason = '' | 'ended' | 'expired' | 'operator_revoked' | 'company_deleted';

export interface PersonRef {
  id: number;
  name: string;
}

/** 運営の画面のサポートの記録（入る・戻る・一覧・終わらせるの応答）。 */
export interface SupportAccess {
  id: number;
  company: { id: number; name: string };
  operator: PersonRef;
  support_user: number;
  status: SupportStatus;
  created_at: string;
  expires_at: string;
  last_entered_at: string | null;
  ended_at: string | null;
  ended_by: PersonRef | null;
  end_reason: SupportEndReason;
}

/** お客様の管理者に見せる、今のサポート（運営の個人の名前は無い）。 */
export interface CompanySupportAccess {
  id: number;
  name: string;
  support_user: number;
  created_at: string;
  /** 記録の期限（作ってから24時間）。 */
  expires_at: string;
  /**
   * 実際に効く期限。min(expires_at, 運営のログインの締め切り)。帯の support_until と同じ値（2026-10-26）。
   * サーバーは必ず返す（契約で必須）。古いサーバーの応答に無ければ expires_at を使う（customerDeadline）。
   */
  effective_until?: string;
}

/**
 * お客様に見せる期限（2026-10-26 のステージングの確かめ）。帯は support_until（19:38）、ダッシュボードは
 * expires_at（19:42）と、同じサポートの期限がずれていた。お客様には、実際に効く期限（effective_until）を見せる。
 */
export function customerDeadline(row: CompanySupportAccess): string {
  return row.effective_until ?? row.expires_at;
}

export interface SupportPage {
  count: number;
  results: SupportAccess[];
}

// ---- サポートのセッションか（access の claim） ----------------------------------------------------

export type { SupportClaims };

/** このタブの access がサポートのセッションのものなら、その claim。違えば null。 */
export function supportClaims(): SupportClaims | null {
  try {
    return supportClaimsOf(getAccessToken());
  } catch {
    // access を読めない（読み込みの途中など）ときは、サポートのセッションとしない。
    return null;
  }
}

/** このタブが今サポートのセッションか（帯・ログアウトの置き換え・出さない導線の判定）。 */
export function isSupportSession(): boolean {
  return supportClaims() !== null;
}

/**
 * 帯に出す期限。トークンの support_until（サーバーが min(サポートの期限, 運営の締め切り) を入れる。#393）を使う。
 * support_until の無いトークンでは、サポートの期限（expires_at）と運営のログインの締め切り（otp_deadline）の早い方。
 * どれも分からなければ null。
 */
export function supportDeadline(expiresAt: string | null | undefined, claims: SupportClaims | null): number | null {
  if (claims?.supportUntil != null) return claims.supportUntil;
  const expires = expiresAt ? Date.parse(expiresAt) : NaN;
  const candidates = [Number.isNaN(expires) ? null : expires, claims?.otpDeadline ?? null].filter(
    (value): value is number => value !== null
  );
  return candidates.length ? Math.min(...candidates) : null;
}

// ---- 入ったときの記録（会社名・期限。トークンではない） ---------------------------------------------

const INFO_KEY = 'supportAccessInfo';

/**
 * 入る・戻るの応答の記録を、そのセッション（「sid:user_id」）と組にして覚える（タブ間で共有。秘密ではない）。
 * 帯の会社名・期限と、戻った後に開く会社の詳細に使う。別のセッションのものは読まない。
 */
export function rememberSupportAccess(access: SupportAccess): void {
  try {
    localStorage.setItem(INFO_KEY, JSON.stringify({ session: getSessionIdentity(), access }));
  } catch {
    /* 保存できない環境では、帯はサーバーから読み直す */
  }
}

export function readSupportAccess(): SupportAccess | null {
  try {
    const raw = localStorage.getItem(INFO_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.session !== getSessionIdentity() || typeof parsed?.access?.id !== 'number') return null;
    return parsed.access as SupportAccess;
  } catch {
    return null;
  }
}

export function forgetSupportAccess(): void {
  try {
    localStorage.removeItem(INFO_KEY);
  } catch {
    /* 消せない環境では何もしない（別のセッションでは読まない） */
  }
}

// ---- 入る・戻る -----------------------------------------------------------------------------------

/**
 * 入る・戻るの応答の access を、セッションとして使えなかった（establishSessionWithinLock が not_manager・
 * cookie_unavailable・network を返した）。このときセッションは後始末済み（印を消し、Cookie を失効させた）なので、
 * 運営はもう一度ログインする。
 */
export class SupportSessionLostError extends Error {
  readonly outcome: LoginOutcome;
  constructor(outcome: LoginOutcome) {
    super(`The support session could not be established (${outcome})`);
    this.name = 'SupportSessionLostError';
    this.outcome = outcome;
  }
}

/** 応答が決まった形でない（access・support_access が無い）。 */
export class UnexpectedSupportResponseError extends Error {
  constructor() {
    super('The support response was not in the expected form');
    this.name = 'UnexpectedSupportResponseError';
  }
}

function readSwitch(response: { data?: any } | undefined): { access: string; record: SupportAccess } {
  const access = response?.data?.access;
  const record = response?.data?.support_access;
  if (typeof access !== 'string' || access === '' || typeof record?.id !== 'number') {
    throw new UnexpectedSupportResponseError();
  }
  return { access, record };
}

/**
 * 運営が、その会社の管理者として入る。成功したら、このタブ（とほかのタブ）のセッションはサポートのものになる。
 * 呼び出し側は、成功の後に管理者の画面を開き直す（access はメモリだけなので、開き直すと Cookie で取り直す）。
 */
export async function enterSupport(companyId: number): Promise<SupportAccess> {
  if (isSessionReplaced()) throw new SessionChangedError();
  const session = getSessionIdentity();
  // 運営の access を Bearer で送る。期限が切れていれば、先に Cookie で取り直す。
  if (!hasUsableAccess()) await refreshAccessToken(session);
  return withSessionLock(async () => {
    if (getSessionIdentity() !== session) throw new SessionChangedError();
    const sid = sidOf(session);
    if (!sid) throw new SessionChangedError();
    const operatorAccess = getAccessToken();
    // #425 の Codex 4392002894。前の画面の同じセッションの更新が途中なら、古い Cookie の 401 の後に取り直す。
    const response = await postRecoveringInterruptedRefresh(() =>
      postWebSession(
        webSessionPath(sid, 'support/enter'),
        { company: companyId },
        false,
        operatorAccess ? { Authorization: `Bearer ${operatorAccess}` } : {}
      )
    );
    const { access, record } = readSwitch(response);
    const outcome = await establishSessionWithinLock(access);
    if (outcome !== 'ok') throw new SupportSessionLostError(outcome);
    rememberSupportAccess(record);
    return record;
  });
}

/**
 * 運営の画面に戻る（end が true なら、サポートも終わらせる）。資格は Cookie だけ（サポートが終わった後でも送れる）。
 * 成功したら、このタブのセッションは入ったときの運営のセッションになる。戻った記録（どの会社か）を返す。
 */
export function returnFromSupport(end = false): Promise<SupportAccess> {
  // 戻り始めたら、前の要求の 401 support_access_ended で終わりの画面へ移さない（config/supportSession）。
  // 戻れなかったら元に戻す（サポートのセッションのまま、終わりの扱いを続ける）。成功したら画面を開き直すので戻さない。
  markLeavingSupport(true);
  return withSessionLock(async () => {
    // 戻るはサポートのセッションの経路へ（資格はその Path の Cookie だけ）。セッションの印が無ければ、どの Cookie も
    // 送れない（Cookie が無いときの 401 refresh_cookie_missing と同じく、もう一度ログインしてもらう）。
    const sid = sidOf(getSessionIdentity());
    if (!sid) throw new SupportSessionLostError('cookie_unavailable');
    // #425 の Codex 4392002894。サポートのセッションの更新が途中で画面を離れていたら、回転した Cookie が届くのを待って
    // 取り直す（古い Cookie の 401 token_not_valid だけ。すぐにログインし直しにしない）。
    const response = await postRecoveringInterruptedRefresh(() =>
      postWebSession(webSessionPath(sid, 'support/return'), { end })
    );
    const { access, record } = readSwitch(response);
    const outcome = await establishSessionWithinLock(access);
    if (outcome !== 'ok') throw new SupportSessionLostError(outcome);
    forgetSupportAccess();
    return record;
  }).catch((error) => {
    markLeavingSupport(false);
    throw error;
  });
}

// ---- 運営の画面の一覧・終わらせる ---------------------------------------------------------------------

/** サポートの一覧（新しい順・1ページ10件）。company を渡すとその会社だけ。status は active（今効いている）か all。 */
export async function listSupportAccesses(options: {
  page: number;
  status: 'active' | 'all';
  company?: number;
}): Promise<SupportPage> {
  const company = options.company !== undefined ? `&company=${options.company}` : '';
  const response = await axiosInstance.get(
    `${OPERATOR_SUPPORT_URL}?page=${options.page}&page_size=${SUPPORT_PAGE_SIZE}&status=${options.status}${company}`
  );
  const data = response.data;
  return {
    count: typeof data?.count === 'number' ? data.count : 0,
    results: Array.isArray(data?.results) ? data.results : [],
  };
}

/**
 * サポートを終わらせる（どの運営のものも）。X-Request-ID を付ける（サーバーは同じ値の再試行に前回の応答を返す）。
 * すでに閉じていれば、サーバーは何もせず同じ形で返す。requestId は、確認のダイアログの中の再試行で同じ値を使う。
 */
export async function endSupportAccess(id: number, requestId: string = newRequestId()): Promise<SupportAccess> {
  const response = await axiosInstance.post(`${OPERATOR_SUPPORT_URL}${id}/end/`, undefined, {
    headers: { 'X-Request-ID': requestId },
  });
  return response.data;
}

export { newRequestId };

// ---- お客様の管理者の画面 ------------------------------------------------------------------------------

/** 自分の会社の、今効いているサポート（期限の早い順）。 */
export async function listCompanySupportAccesses(): Promise<CompanySupportAccess[]> {
  const response = await axiosInstance.get(COMPANY_SUPPORT_URL);
  return Array.isArray(response.data) ? response.data : [];
}

export interface SupportProfileSummary {
  name: string;
  company: string | null;
}

/** サポートのセッションの名前（「運営のサポート」）と会社名（プロフィール）。帯・左のプロフィールで使う。 */
export async function getSupportProfile(): Promise<SupportProfileSummary> {
  const response = await axiosInstance.get('/v1/profile/');
  const name = response.data?.name;
  const company = response.data?.company_info?.name;
  return { name: typeof name === 'string' ? name : '', company: typeof company === 'string' ? company : null };
}

/** 今のログインの利用者の id（セッションの印「sid:user_id」から）。分からなければ null。 */
export function currentUserId(): number | null {
  const identity = getSessionIdentity();
  if (!identity) return null;
  const value = Number(identity.slice(identity.indexOf(':') + 1));
  return Number.isInteger(value) && value > 0 ? value : null;
}

// ---- 画面の移動（試験で差し替える） ----------------------------------------------------------------------

/** 入る・戻るの後は、ログインの後と同じく画面を開き直す（access はメモリだけ。Cookie で取り直す）。 */
export const supportNavigation = {
  assign: (path: string) => {
    window.location.assign(path);
  },
};
